import { Chip } from '@plaidev/sour-react';
import type { ReactElement } from 'react';

import type { ImportTaskSummary } from './types';

type SummaryMetricProps = {
  label: string;
  value: number | string;
  description?: string;
  tone?: 'default' | 'positive' | 'warning' | 'negative';
  emphasis?: boolean;
  labelChipClass?: string;
  labelClassName?: string;
};

function formatExtractedCount(value: number | null | undefined): string {
  return typeof value === 'number' ? `${value}件` : '-';
}

function SummaryMetric({
  label,
  value,
  description,
  tone = 'default',
  emphasis = false,
  labelChipClass,
  labelClassName,
}: SummaryMetricProps) {
  const valueClass =
    tone === 'positive' ? 'text-positive' : tone === 'warning' ? 'text-warning' : tone === 'negative' ? 'text-negative' : 'text-default';

  return (
    <div className={`rounded-lg border border-default bg-default bg-surface p-4 ${emphasis ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
      <dt className={`text-xs font-medium text-muted-light ${labelClassName ?? ''}`}>
        {labelChipClass ? (
          <Chip as="span" size="xs" variant="filled" className={labelChipClass}>
            {label}
          </Chip>
        ) : (
          label
        )}
      </dt>
      <dd className={`mt-2 font-semibold tabular-nums ${emphasis ? 'text-4xl leading-none' : 'text-2xl'} ${valueClass}`}>{value}</dd>
      {description ? <p className="mt-2 text-xs text-muted-light">{description}</p> : null}
    </div>
  );
}

function TotalRowsMetric({ value, description }: { value: number; description?: string }) {
  return (
    <div className="rounded-lg border border-default bg-default bg-surface p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>{description ? <p className="text-xs text-muted-light">{description}</p> : null}</div>
        <dd className="flex items-baseline gap-1 text-default">
          <span className="text-base font-medium text-muted-light">全</span>
          <span className="font-semibold tabular-nums text-4xl leading-none">{value}</span>
          <span className="text-base font-medium text-muted-light">件</span>
        </dd>
      </div>
    </div>
  );
}

type SummarySectionProps = {
  summary: ImportTaskSummary | undefined;
};

export function SummarySection({ summary }: SummarySectionProps): ReactElement {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="m-0 text-base font-semibold text-default">件数サマリ</h2>
        {summary ? <p className="m-0 text-xs text-muted-light">CSV の取り込み結果を件数で表示しています。</p> : null}
      </div>
      {summary ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(280px,1fr)_minmax(0,3fr)]">
          <section className="rounded-xl border border-default p-4">
            <h3 className="m-0 text-sm font-semibold text-default">抽出結果</h3>
            <p className="mt-1 text-xs text-muted-light">成功行から識別できた一意な数です。</p>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <SummaryMetric
                label="契約"
                value={formatExtractedCount(summary.extractedContracts)}
                labelChipClass="bg-notice-light text-notice"
              />
              <SummaryMetric
                label="顧客"
                value={formatExtractedCount(summary.extractedCustomers)}
                labelChipClass="bg-primary-light text-primary"
              />
            </dl>
          </section>

          <section className="rounded-xl border border-default p-4">
            <h3 className="m-0 text-sm font-semibold text-default">処理件数</h3>
            <p className="mt-1 text-xs text-muted-light">行単位の結果です。</p>
            <dl className="mt-4 flex flex-col gap-3">
              <TotalRowsMetric value={summary.totalRows} description="読み込んだ行数" />
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryMetric
                  label="新規"
                  value={summary.created}
                  tone="positive"
                  description="顧客・契約がどちらも新規の行"
                  labelClassName="font-semibold"
                />
                <SummaryMetric
                  label="更新"
                  value={summary.updated}
                  description="顧客または契約が既存だった行"
                  labelClassName="font-semibold"
                />
                <SummaryMetric
                  label="スキップ"
                  value={summary.skipped}
                  tone="warning"
                  description="取り込み対象外"
                  labelClassName="font-semibold"
                />
                <SummaryMetric
                  label="失敗"
                  value={summary.failed}
                  tone="negative"
                  description="取り込めなかった行"
                  labelClassName="font-semibold"
                />
              </div>
            </dl>
          </section>
        </div>
      ) : (
        <p className="rounded-lg border border-default bg-subtle-light px-4 py-6 text-sm text-muted-light">サマリはまだありません。</p>
      )}
    </section>
  );
}
